import {Injectable} from '@angular/core';
import {App, AppId} from '../../shared/models/app';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {Filters} from '../../shared/models/filter';
import {UrlParams} from '../../shared/models/url';

export const URL_PARAM_KEY_SEARCH_TERM = 's';
export const URL_PARAM_KEY_PAGE_NUMBER = 'p';
export const URL_PARAM_KEY_FILTERS = 'f';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  private searchTerm: string;
  private pageNumber: number;
  private filters: Filters;

  readonly searchTerm$: Observable<string> = new Observable<string>(
    subscriber => {
      const sub = this._searchTerm$.subscribe({
        next: (searchTerm) => {
          subscriber.next(searchTerm);
        },
      });

      return () => {
        sub.unsubscribe();
      };
    },
  );
  readonly pageNumber$: Observable<number> = new Observable<number>(
    subscriber => {
      const sub = this._pageNumber$.subscribe({
        next: (pageNumber) => {
          subscriber.next(pageNumber);
        },
      });

      return () => {
        sub.unsubscribe();
      };
    },
  );
  readonly filters$: Observable<Filters> = new Observable<Filters>(
    subscriber => {
      const sub = this._filters$.subscribe({
        next: (filters) => {
          subscriber.next(filters);
        },
      });

      return () => {
        sub.unsubscribe();
      };
    },
  );

  private _searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private _pageNumber$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  private _filters$: BehaviorSubject<Filters> = new BehaviorSubject<Filters>(undefined);

  /**
   * Since contents of params are encoded JSON strings and strings are immutable,
   * storing the last params we got at any given time enables us to compare new
   * and previous params in a way that we could not after JSON.parse is called
   * since some of the params could be of mutable types.
   *
   * @private
   */
  private lastParams: Params = {};


  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.route.queryParams.subscribe({
      next: (params) => {
        const searchTermChanged = params[URL_PARAM_KEY_SEARCH_TERM] !== this.lastParams[URL_PARAM_KEY_SEARCH_TERM];
        const pageNumberChanged = params[URL_PARAM_KEY_PAGE_NUMBER] !== this.lastParams[URL_PARAM_KEY_PAGE_NUMBER];
        const filtersChanged = params[URL_PARAM_KEY_FILTERS] !== this.lastParams[URL_PARAM_KEY_FILTERS];

        // We can set this now that all the comparisons between old and new are complete
        this.lastParams = params;

        const urlParams: UrlParams = {
          searchTerm: params[URL_PARAM_KEY_SEARCH_TERM] ? JSON.parse(params[URL_PARAM_KEY_SEARCH_TERM]) : undefined,
          pageNumber: params[URL_PARAM_KEY_PAGE_NUMBER] ? JSON.parse(params[URL_PARAM_KEY_PAGE_NUMBER]) : undefined,
          filters: params[URL_PARAM_KEY_FILTERS] ? JSON.parse(params[URL_PARAM_KEY_FILTERS]) : undefined,
        };

        this.searchTerm = urlParams.searchTerm;
        this.pageNumber = urlParams.pageNumber;
        this.filters = urlParams.filters;

        if (searchTermChanged) {
          this._searchTerm$.next(this.searchTerm ?? '');
        }

        if (pageNumberChanged) {
          this._pageNumber$.next(this.pageNumber ?? 1);
        }

        if (filtersChanged) {
          this._filters$.next(this.filters);
        }
      },
    });
  }


  navigateByUrl(url: string) {
    this.router.navigateByUrl(url);
  }


  setPageNumber(p: number, replaceUrl = false) {
    if (p <= 1) {
      if (this.pageNumber !== undefined) {
        this.deleteQueryParam(URL_PARAM_KEY_PAGE_NUMBER);
      }
    } else {
      this.upsertQueryParam(URL_PARAM_KEY_PAGE_NUMBER, p, replaceUrl);
    }
  }


  setSearchTerm(s: string, replaceUrl = false) {
    if (!s && !this.searchTerm) {
      return;
    }

    const params: Params = {};

    params[URL_PARAM_KEY_SEARCH_TERM] = s ? JSON.stringify(s) : null;
    params[URL_PARAM_KEY_PAGE_NUMBER] = null;

    this.router.navigate(
      [],
      {
        replaceUrl,
        relativeTo: this.route,
        queryParams: params,
        queryParamsHandling: 'merge',
      },
    );
  }


  setFilters(f: Filters, replaceUrl = false) {
    if (!f || !Object.values(f).length) {
      if (this.filters) {
        this.deleteQueryParam(URL_PARAM_KEY_FILTERS);
      }
    } else {
      let atLeastOneValue: boolean = false;

      for (let i in f) {
        // Handle arrays (devices, hubs, owners)
        if (Array.isArray(f[i]) && !f[i].length) {
          delete f[i];
          continue;
        }

        // Handle booleans (metadata)
        if (typeof f[i] === 'boolean' && !f[i]) {
          delete f[i];
          continue;
        }

        atLeastOneValue = true;
      }

      if (atLeastOneValue) {
        this.upsertQueryParam(URL_PARAM_KEY_FILTERS, f, replaceUrl);
      } else if (this.filters) {
        this.deleteQueryParam(URL_PARAM_KEY_FILTERS);
      }
    }
  }


  private getQueryParam$<T>(paramName: string): Observable<T> {
    return new Observable<T>(
      subscriber => {
        this.route.queryParamMap.subscribe({
          next: (paramMap) => {
            if (paramMap.has(paramName)) {
              const value = paramMap.get(paramName);
              subscriber.next(JSON.parse(value));
            } else {
              subscriber.next(undefined);
            }
          },
        });
      },
    );
  }


  /**
   *
   * @param paramName
   * @param value
   * @param replaceUrl {boolean} Same as replaceUrl in Angular router
   *    interface called NavigationBehaviorOptions
   */
  private upsertQueryParam(paramName: string, value: any, replaceUrl = false) {
    const params = {};
    params[paramName] = JSON.stringify(value);

    this.router.navigate(
      [],
      {
        replaceUrl: replaceUrl,
        relativeTo: this.route,
        queryParams: params,
        queryParamsHandling: 'merge',
      },
    );
  }


  deleteQueryParam(paramName: string) {
    const params = {};
    params[paramName] = null;

    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: params,
        queryParamsHandling: 'merge',
      },
    );
  }


  static controlsUrls: {
    dashboard: string,
    devices: string,
    devicesExisting: string,
    devicesNew: string,
    hubs: string,
    hubsExisting: string,
    hubsNew: string,
    tiles: string,
    tilesExisting: string,
    tilesNew: string,
  } = {
    dashboard: '/controls/dashboard',
    devices: '/controls/devices',
    devicesExisting: '/controls/devices/existing',
    devicesNew: '/controls/devices/new',
    hubs: '/controls/hubs',
    hubsExisting: '/controls/hubs/existing',
    hubsNew: '/controls/hubs/new',
    tiles: '/controls/tiles',
    tilesExisting: '/controls/tiles/existing',
    tilesNew: '/controls/tiles/new',
  };


  static makeAppsHomeUrl(): string {
    return `/apps/home`;
  }


  static makeAppsManageUrl(): string {
    return `/apps/manage`;
  }


  static makeAppsManageExistingUrl(): string {
    return `/apps/manage/existing`;
  }


  static makeAppsManageNewUrl(): string {
    return `/apps/manage/new`;
  }


  static makeAppUrl(app: App): string {
    switch (app.Type) {
      case 'picture': {
        return this.makeMosaicDashboardUrl(app.ID);
      }
      case 'prop-manager': {
        return this.makePropManagerManageUnitsUrl(app.ID);
      }
      case 'tenant-portal': {
        return this.makeTenantPortalDashboardUrl(app.ID);
      }
    }
  }


  static makePropManagerManageUnitsUrl(appId: AppId): string {
    return `/prop-manager/${appId}/manage/units`;
  }


  static makePropManagerManageUnitUsersUrl(appId: AppId, tenantAppId: AppId): string {
    return `/prop-manager/${appId}/manage/units/${tenantAppId}/users`;
  }


  static makePropManagerManageUnitConfigurationUrl(appId: AppId, tenantAppId: AppId): string {
    return `/prop-manager/${appId}/manage/units/${tenantAppId}/configuration`;
  }


  static makePropManagerStaffUrl(appId: AppId): string {
    return `/prop-manager/${appId}/manage/staff`;
  }


  static makeTenantPortalDashboardUrl(appId: AppId): string {
    return `/tenant-portal/${appId}/dashboard`;
  }


  static makeTenantPortalContactLandlordUrl(appId: AppId): string {
    return `/tenant-portal/${appId}/contact-landlord`;
  }


  static makeMosaicDashboardUrl(appId: AppId): string {
    return `/mosaic/${appId}/dashboard`;
  }


  static makeMosaicConfigurationUrl(appId: AppId): string {
    return `/mosaic/${appId}/configuration`;
  }


  static makeMosaicConfigurationTileGroupsUrl(appId: AppId): string {
    return `/mosaic/${appId}/configuration/tile-groups`;
  }


  static makeMosaicConfigurationAppUrl(appId: AppId): string {
    return `/mosaic/${appId}/configuration/app`;
  }


  static makeMosaicConfigurationTilesUrl(appId: AppId): string {
    return `/mosaic/${appId}/configuration/tiles`;
  }


  static makeAlarmsDashboardUrl(): string {
    return `/alarms/dashboard`;
  }


  static makeAlarmsHistoryUrl(): string {
    return `/alarms/history`;
  }


  static makeAlarmEventDetailsUrl(eventUuid: string): string {
    return `/alarms/events/${eventUuid}`;
  }
}
